import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Divider, Grid, Stack, Typography } from '@mui/material';

import { ICustodyCheckoutPersonReq, MasterNameApi, UserApi } from '@/api';
import {
  FormDateField,
  FormInputField,
  FormPatternField,
  MasterNameAutocomplete,
  NoResultItem,
  RenderFormField,
  SelectItem,
} from '@/components';
import {
  IMasterName,
  IMasterNamePerson,
  IMasterNameReq,
  MasterNameType,
  PhoneNumberType,
  Role,
} from '@/models';
import { uploadSignatureFile } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { useUserStore } from '@/store/store.user';
import { parsePhoneNumber } from '@/utils';

import { CustodyActionSection } from './CustodyActionSection';
import { ToPersonFormFields } from './data';
import { PropertyModalFooter } from './PropertyModalFooter';
import { SideModalTitle } from './SideModalTitle';
import {
  getCustodyActionDefaultValues,
  normalizeCustodyActionDateTime,
  useCustodyOfficerOptions,
} from './useCustodyActionForm';
import { useKasePropertyContext } from '../../PropertyContext';

interface ToPersonFormValues extends ICustodyCheckoutPersonReq {
  personType: 'ExternalPerson' | 'AgencyPersonnel';
  agencyPersonId: string;
  externalFirstName: string;
  externalLastName: string;
  externalDob: string;
  externalPhone: string;
  dateExpectedBack?: string;
}

const DefaultCheckoutToPersonValues = {
  actionDateTime: '',
  personType: 'ExternalPerson' as const,
  notes: '',
  officer: '',
  dateExpectedBack: '',
  agencyPersonId: '',
  externalFirstName: '',
  externalLastName: '',
  externalDob: '',
  externalPhone: '',
  signature: '',
};

export const ToPersonForm = () => {
  const { updateToast } = useToastStore();
  const { currentPropertyId, custodyModalValues, parentId, sourceAdapter } =
    useKasePropertyContext();
  const { user } = useUserStore();

  const [agencyPersons, setAgencyPersons] = useState<SelectItem[]>([]);
  const [selectedMasterPerson, setSelectedMasterPerson] =
    useState<IMasterNamePerson | null>(null);
  const [isAddingNewPerson, setIsAddingNewPerson] = useState(false);
  const [autocompleteKey, setAutocompleteKey] = useState(0);

  const { officerOptions } = useCustodyOfficerOptions();

  const methods = useForm<ToPersonFormValues>({
    defaultValues: {
      ...DefaultCheckoutToPersonValues,
      ...getCustodyActionDefaultValues(user),
    },
    mode: 'all',
  });
  const { getValues, watch, trigger, setValue } = methods;

  const personTypeValue = watch('personType');

  useEffect(() => {
    fetchAgencyPoliceUsers();
  }, []);

  useEffect(() => {
    if (personTypeValue === 'AgencyPersonnel') {
      setSelectedMasterPerson(null);
      setIsAddingNewPerson(false);
      setAutocompleteKey((k) => k + 1);
      (
        [
          'externalFirstName',
          'externalLastName',
          'externalDob',
          'externalPhone',
        ] as const
      ).forEach((f) => setValue(f, ''));
    }
  }, [personTypeValue]);

  const fetchAgencyPoliceUsers = async () => {
    try {
      const res = await UserApi.get({
        limit: 1000,
        filter: JSON.stringify({
          'roles.role': {
            $in: [Role.RMS_CASES_FULL_ACCESS, Role.RMS_CASES_VIEW],
          },
        }),
      });
      const agencyPersonOptions = res.data.results.map(
        ({ _id, fullName, email }) => ({
          label: fullName || email,
          value: _id || '',
        }),
      );
      setAgencyPersons(agencyPersonOptions);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const handleMasterPersonSelected = useCallback(
    (value: IMasterName) => {
      const person = value as IMasterNamePerson;
      setSelectedMasterPerson(person);
      setIsAddingNewPerson(false);
      setValue('externalFirstName', person.firstName ?? '');
      setValue('externalLastName', person.lastName ?? '');
      setValue('externalDob', person.birthday ?? '');
      const mobile = person.phoneNumbers?.find(
        (p) => p.type === PhoneNumberType.MOBILE,
      );
      const raw = (mobile ?? person.phoneNumbers?.[0])?.phoneNumber ?? '';
      setValue('externalPhone', raw.replace(/^\+1/, ''));
    },
    [setValue],
  );

  const handleAddNew = useCallback(() => {
    setIsAddingNewPerson(true);
    setSelectedMasterPerson(null);
    setAutocompleteKey((k) => k + 1);
    (
      [
        'externalFirstName',
        'externalLastName',
        'externalDob',
        'externalPhone',
      ] as const
    ).forEach((f) => setValue(f, ''));
  }, [setValue]);

  const handleClear = useCallback(() => {
    setSelectedMasterPerson(null);
    setIsAddingNewPerson(false);
    (
      [
        'externalFirstName',
        'externalLastName',
        'externalDob',
        'externalPhone',
      ] as const
    ).forEach((f) => setValue(f, ''));
  }, [setValue]);

  const onCheckoutToPerson = async () => {
    const isValid = await trigger();
    const { bulkSelection } = custodyModalValues;
    const properties =
      bulkSelection ?? (currentPropertyId ? [currentPropertyId] : []);
    if (!isValid || !parentId || !properties) return false;

    const values = getValues();
    const payload: ICustodyCheckoutPersonReq = {
      actionDateTime: normalizeCustodyActionDateTime(
        values.actionDateTime,
      ) as string,
      officer: values.officer,
      notes: values.notes,
      expectedBackDate: values.dateExpectedBack,
    };

    if (values.personType === 'ExternalPerson') {
      if (isAddingNewPerson) {
        const newPersonData: Partial<IMasterNameReq> = {
          firstName: values.externalFirstName,
          lastName: values.externalLastName,
          ...(values.externalDob ? { birthday: values.externalDob } : {}),
          ...(values.externalPhone
            ? {
                phoneNumbers: [
                  {
                    phoneNumber: parsePhoneNumber(values.externalPhone),
                    type: PhoneNumberType.MOBILE,
                  },
                ],
              }
            : {}),
        };
        const res = await MasterNameApi.createMasterNamePerson(
          newPersonData as IMasterNameReq,
        );
        if (!res.data._id) {
          updateToast({
            open: true,
            type: ConfirmTypes.ERROR,
            message: 'Failed to create person. Please try again.',
          });
          return false;
        }
        payload.masterName = res.data._id;
      } else {
        if (!selectedMasterPerson) return false;
        payload.masterName = selectedMasterPerson._id;
      }
    } else {
      payload.user = values.agencyPersonId;
    }

    if (values.signature) {
      const signatureFile = await uploadSignatureFile(
        values.signature as string,
        'checkout-property-signature.png',
      );
      if (signatureFile) {
        payload.signature = signatureFile[0];
      }
    }

    await sourceAdapter.custodyCheckoutPerson(parentId, properties, payload);
    updateToast({
      open: true,
      type: ConfirmTypes.SUCCESS,
      message: 'Property is checked out to person successfully.',
    });
    return true;
  };

  const showPersonDetails = selectedMasterPerson !== null || isAddingNewPerson;
  const fieldsDisabled = !isAddingNewPerson;

  return (
    <FormProvider {...methods}>
      <Stack
        style={{
          height: 'calc(100vh - 120px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
        }}
      >
        <SideModalTitle>To Person</SideModalTitle>

        <Grid container>
          <Grid item md={12}>
            <CustodyActionSection officerOptions={officerOptions} />
          </Grid>

          {/* personType radios */}
          <Grid item md={12}>
            <RenderFormField
              name="personType"
              type="radios"
              items={[
                { value: 'ExternalPerson', label: 'External person' },
                { value: 'AgencyPersonnel', label: 'Agency personnel' },
              ]}
            />
          </Grid>

          {/* ── External Person branch ── */}
          {personTypeValue === 'ExternalPerson' && (
            <>
              <Grid item md={12} sx={{ my: 2 }}>
                <Typography fontWeight="500">Personal details</Typography>
              </Grid>
              <Grid item md={12}>
                <MasterNameAutocomplete
                  key={autocompleteKey}
                  filterByType={MasterNameType.PERSON}
                  includeUnverified
                  clearable
                  onChange={handleMasterPersonSelected}
                  onClear={handleClear}
                  value={selectedMasterPerson ?? undefined}
                  noResults={
                    <NoResultItem
                      flexDirection="row"
                      alignItems="center"
                      sx={{ ml: -1 }}
                    >
                      <Button
                        onClick={handleAddNew}
                        variant="text"
                        color="error"
                      >
                        + Add new
                      </Button>
                    </NoResultItem>
                  }
                />
              </Grid>

              {showPersonDetails && (
                <>
                  <Grid item md={6} sx={{ mt: 2, pr: 1 }}>
                    <FormInputField
                      name="externalLastName"
                      label="Last name"
                      disabled={fieldsDisabled}
                      rules={
                        isAddingNewPerson
                          ? { required: 'This field is required.' }
                          : {}
                      }
                    />
                  </Grid>
                  <Grid item md={6} sx={{ mt: 2, pl: 1 }}>
                    <FormInputField
                      name="externalFirstName"
                      label="First name"
                      disabled={fieldsDisabled}
                      rules={
                        isAddingNewPerson
                          ? { required: 'This field is required.' }
                          : {}
                      }
                    />
                  </Grid>
                  <Grid item md={12} sx={{ mt: 2 }}>
                    <FormDateField
                      name="externalDob"
                      label="Date of birth"
                      disabled={fieldsDisabled}
                    />
                  </Grid>
                  <Grid item md={6} sx={{ mt: 2, pr: 1 }}>
                    <FormPatternField
                      name="externalPhone"
                      label="Phone number"
                      format="(###) ###-####"
                      disabled={fieldsDisabled}
                      rules={{
                        pattern: {
                          value: /[2-9][0-9]{9}$/im,
                          message: 'Invalid phone number.',
                        },
                      }}
                    />
                  </Grid>
                  <Grid item md={6} sx={{ mt: 2, pl: 1 }}>
                    <FormDateField
                      name="dateExpectedBack"
                      label="Date expected back"
                    />
                  </Grid>
                </>
              )}
            </>
          )}

          {/* ── Agency Personnel branch ── */}
          {personTypeValue === 'AgencyPersonnel' && (
            <>
              <Grid item md={12} sx={{ my: 2 }}>
                <Typography fontWeight="500">Personal details</Typography>
              </Grid>
              <Grid item md={12}>
                <RenderFormField
                  name="agencyPersonId"
                  type="autocomplete"
                  label="Name"
                  items={agencyPersons}
                  rules={{ required: 'This field is required.' }}
                />
              </Grid>
              <Grid item md={12} sx={{ mt: 2 }}>
                <FormDateField
                  name="dateExpectedBack"
                  label="Date expected back"
                />
              </Grid>
            </>
          )}

          {/* signature and notes */}
          {ToPersonFormFields.map(({ name, ...rest }) => (
            <Grid key={name} item md={12}>
              <Divider sx={{ margin: '10px 0 20px', width: '100%' }} />
              <RenderFormField {...rest} name={name} />
            </Grid>
          ))}
        </Grid>

        <PropertyModalFooter onSave={onCheckoutToPerson} />
      </Stack>
    </FormProvider>
  );
};
